import { useCallback, useEffect, useState } from "react"
import { Navigate, useLocation } from "react-router-dom"
import AppContext from "../AppContext.jsx"
import api from "../api.js"
import auth from "../db/auth.js"
import registerPeriodicSync from "../serviceWorker/periodicSync"
import useNewServiceWorker from "../serviceWorker/useNewServiceWorker"
import useUpdateAvailable from "../serviceWorker/useUpdateAvailable"
import listenForReload from "../serviceWorker/listenForReload"
import syncDb from "../serviceWorker/syncDb"
import swReady from "../serviceWorker/ready.js"
import useInterval from "../utils/useInterval"
import consoleLogger from "../services/consoleLogger.js"
import {
  installServerReachabilityListener,
  reportServerReachable,
  subscribeServerReachability,
} from "../serverReachability.js"

const CHECK_BUILD_INTERVAL = Number(import.meta.env.VITE_CHECK_BUILD_INTERVAL)

export default function V2AppProvider({ children }) {
  const location = useLocation()
  const [app, setApp] = useState({
    connected: true,
    offlineCapabilities: false,
    updateAvailable: false,
    updateSelected: false,
    loaded: false,
    locations: [],
    species: [],
    processes: [],
    weighInProcesses: [],
    options: [],
    recordLogs: false,
    featureFlags: [],
    locationId: 0,
  })

  const newServiceWorker = useNewServiceWorker()
  const updateAvailable = useUpdateAvailable(CHECK_BUILD_INTERVAL)

  useEffect(() => {
    return installServerReachabilityListener()
  }, [])

  useEffect(() => {
    return subscribeServerReachability((troubleReachingServer) => {
      const connected = !troubleReachingServer
      setApp((prev) => {
        if (prev.connected === connected) {
          return prev
        }

        console.log("Online status changed: connected: " + connected)

        if (connected) {
          syncDb()
        }

        return { ...prev, connected }
      })
    })
  }, [])

  const runHealthcheck = useCallback(async () => {
    await api.canReachServer()
  }, [])

  useInterval(runHealthcheck, 30 * 1000, true)
  useInterval(() => syncDb(), 5 * 60 * 1000)

  useEffect(() => {
    // Refresh the page if the controlling service worker has changed (e.g. after activating an update).
    if (newServiceWorker) {
      window.location.reload()
    }
  }, [newServiceWorker])

  useEffect(() => {
    if (!updateAvailable) {
      return
    }

    reportServerReachable()
    setApp((prev) => (prev.updateAvailable ? prev : { ...prev, updateAvailable: true }))
  }, [updateAvailable])

  useEffect(() => {
    // Keep parity with v1: service worker can request a reload.
    const unregister = listenForReload()
    return () => unregister?.()
  }, [])

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("In development mode, no service worker")
      return
    }

    // Keep parity with v1: proactively sync on startup and enable periodic background sync (installed PWA only).
    syncDb()
    registerPeriodicSync()
  }, [])

  useEffect(() => {
    // Keep parity with v1: initialize console log capture early.
    consoleLogger.init().catch((e) => console.error("Failed to initialize console logger:", e))
  }, [])

  useEffect(() => {
    let cancelled = false
    Promise.resolve()
      .then(async () => {
        // Keep parity with v1: offline capabilities are determined by whether a service worker exists
        // (i.e., production builds / PWA install).
        const swRegistration = await swReady()
        const results = await Promise.all([
          api.getLocations(),
          api.getSpecies(),
          api.getProcesses(),
          api.getWeighInProcesses(),
          api.getOptions(),
          api.canReachServer(),
          api.getFeatureFlags(),
        ])
        return { swRegistration, results }
      })
      .then(({ swRegistration, results }) => {
        const [locations, species, processes, weighInProcesses, options, , featureFlags] = results
        if (cancelled) {
          return
        }
        setApp((prev) => ({
          ...prev,
          locations: locations ?? [],
          species: species?.data ?? species ?? [],
          processes: processes?.data ?? processes ?? [],
          weighInProcesses: weighInProcesses?.data ?? weighInProcesses ?? [],
          options: options?.data ?? options ?? [],
          offlineCapabilities: Boolean(swRegistration),
          featureFlags: featureFlags ?? [],
          locationId: auth.getLocationId(),
          recordLogs: localStorage.getItem("recordLogs") === "true",
          loaded: true,
        }))
      })
      .catch((e) => {
        if (cancelled) {
          return
        }
        console.error("Failed to load v2 app references:", e)
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (!auth.getJwt()) {
    return <Navigate to="/log-in" replace state={{ from: location.pathname }} />
  }

  return (
    <AppContext.Provider value={{ app, setApp }}>
      {children}
    </AppContext.Provider>
  )
}
